import React, { useEffect, useState } from "react";
import { api, resolveImg } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";
import { PageHero } from "@/components/site/PageHero";
import { BookButton } from "@/components/site/BookButton";
import { Seo } from "@/components/site/Seo";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSite } from "@/context/SiteContext";

const GROUPS = [
  { name: "Adults", note: "Everyday care for busy, active lives." },
  { name: "Pregnancy", note: "Gentle, adapted care through every stage." },
  { name: "Postpartum", note: "Rebuilding comfort and capacity after birth." },
  { name: "Infants", note: "Very gentle, low-force care from the start." },
  { name: "Children", note: "Family-friendly visits kids actually enjoy." },
  { name: "Teen Athletes", note: "Movement and recovery for growing bodies." },
  { name: "Parents", note: "Because you can't pour from an empty cup." },
  { name: "Active Families", note: "Keeping the whole household moving well." },
];

export default function FamilyCare() {
  const { home } = useSite();
  const [imgs, setImgs] = useState({});
  useEffect(() => {
    api.get("/content/home").then((r) => setImgs(r.data)).catch(() => {});
  }, []);
  const fam = imgs.family_image || home?.family_image;

  return (
    <div>
      <Seo title="Family Chiropractor in Gardner, KS | Psalm 30:2 Chiropractic" description="Family-centered chiropractic in Gardner, Kansas — pediatric, pregnancy, postpartum and care for the whole household." />
      <PageHero
        eyebrow="Family Care"
        title={<>Care for<br />the whole family.</>}
        intro="Every member of your family runs on a nervous system — from a newborn learning to settle to grandparents staying strong and steady. Psalm 30:2 is strongly family-centered: gentle, personal care that fits real family life."
        image={fam}
      />
      <section className="bg-linen py-24 lg:py-32 px-6 md:px-10 lg:px-14" data-testid="family-groups">
        <div className="max-w-[1500px] mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-8">
          {GROUPS.map((g, i) => (
            <Reveal key={g.name} delay={(i % 4) * 60}>
              <div className="border-t border-espresso/15 pt-6">
                <div className="font-serif text-2xl text-espresso mb-2">{g.name}</div>
                <p className="text-espressosoft/70 font-light text-sm leading-relaxed">{g.note}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Featured family pages */}
      <section className="bg-ivory py-20 lg:py-28 px-6 md:px-10 lg:px-14" data-testid="family-featured">
        <div className="max-w-[1500px] mx-auto grid md:grid-cols-2 gap-6">
          <Reveal>
            <Link to="/pregnancy" className="group block relative overflow-hidden rounded-sm aspect-[16/10]" data-testid="family-pregnancy-card">
              <img src="https://images.unsplash.com/photo-1651663303138-4dc283e15992?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="Pregnancy care" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" loading="lazy" />
              <div className="absolute inset-0 bg-espresso/45 group-hover:bg-espresso/55 transition-colors" />
              <div className="absolute inset-0 flex flex-col justify-end p-8 text-ivory">
                <div className="overline !text-brass mb-2">Pregnancy & Birth</div>
                <h3 className="font-serif text-3xl lg:text-4xl">Support through pregnancy →</h3>
              </div>
            </Link>
          </Reveal>
          <Reveal delay={100}>
            <Link to="/pediatric" className="group block relative overflow-hidden rounded-sm aspect-[16/10]" data-testid="family-pediatric-card">
              <img src="https://images.unsplash.com/photo-1585421079919-44c712bdf839?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="Pediatric care" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" loading="lazy" />
              <div className="absolute inset-0 bg-espresso/45 group-hover:bg-espresso/55 transition-colors" />
              <div className="absolute inset-0 flex flex-col justify-end p-8 text-ivory">
                <div className="overline !text-brass mb-2">Children & Teens</div>
                <h3 className="font-serif text-3xl lg:text-4xl">Gentle pediatric care →</h3>
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-linen py-24 px-6 text-center">
        <Reveal>
          <h2 className="font-serif font-light text-4xl lg:text-5xl text-espresso mb-8 max-w-2xl mx-auto">Bring the whole family in.</h2>
          <BookButton label="Book Family Care" variant="primary" track_label="family-care" testid="family-book-button" />
        </Reveal>
      </section>
    </div>
  );
}
